/**
 * pages/ConfigGlobal/ConfigGlobal.js
 * Page Configuration Globale - Upload répertoire et assignation par catégories
 */

import "./ConfigGlobal.css";

import { UploadGlobalConf } from "../../components/UploadGlobalConf/UploadGlobalConf.js";

import logger from "../../utils/logger.js";

export class ConfigGlobal {
  constructor(state) {
    this.state = state;
    this.element = null;
    this.components = {};

    // Sauvegarder les données traitées en mémoire
    this.savedData = {
      assignedItems: {},
      uploadedFiles: [],
      processingComplete: false,
    };

    this.initComponents();
  }

  initComponents() {
    this.components.upload = new UploadGlobalConf({
      state: this.state,
      title: "Configuration Globale",
      description: "Déposer le répertoire parent et assigner les fichiers aux catégories.",
      onStatusChange: (status, data) => this.handleStatusChange(status, data),
      onDataLoaded: (category, data) => this.handleDataLoaded(category, data),
      onDataRemoved: (category) => this.handleDataRemoved(category),
    });
  }

  /**
   * Gérer les changements de statut du composant upload
   */
  handleStatusChange(status, data) {
    switch (status) {
      case "loading":
        this.state.addToasterMessage("info", data.message || "Traitement en cours...");
        break;

      case "success":
        this.state.addToasterMessage("success", data.message || "Opération réussie");
        break;

      case "error":
        this.state.addToasterMessage("error", data.message || "Erreur lors du traitement");
        break;

      case "idle":
        // État initial ou reset
        break;
    }
  }

  /**
   * Gérer les données chargées avec succès pour chaque catégorie
   */
  handleDataLoaded(category, transformedData) {
    // Mapper vers les sources existantes du state
    const categoryMapping = {
      configTiroirs: "configTiroirs",
      baseROP: "baseROP",
      rapportFiberPvReussite: "rapportFiberPvReussite",
      pvReussite: "pvReussite",
      rapportFiberPvEchec: "rapportFiberPvEchec",
      pvEchec: "pvEchec",
      pvHs: "pvHs",
    };

    const sourceKey = categoryMapping[category];
    if (!sourceKey) {
      logger.warn(
        {
          context: "config-global-category-mapping",
          unmappedCategory: category,
          validCategories: [
            "configTiroirs",
            "baseROP",
            "rapportFiberPvReussite",
            "pvReussite",
            "rapportFiberPvEchec",
            "pvEchec",
            "pvHs",
          ],
          dataKeys: transformedData ? Object.keys(transformedData) : [],
        },
        `Catégorie ${category} non mappée vers une source`
      );
      return;
    }

    // Mettre à jour l'état global avec les données transformées
    this.state.setSourceSuccess(sourceKey, transformedData);

    // Sauvegarder dans la mémoire de la page
    this.savedData.assignedItems[category] = {
      transformedData: transformedData,
      validatedAt: new Date(),
    };

    // Messages spécifiques par catégorie
    const successMessages = {
      configTiroirs: `Configuration Tiroirs: ${transformedData.referenceCount || 0} références`,
      baseROP: `Base ROP: ${transformedData.referenceCount || 0} références`,
      rapportFiberPvReussite: `Rapport PV Réussite: ${transformedData.referenceCount || 0} références`,
      pvReussite: `PV Réussite SOR: ${transformedData.fileCount || 0} fichiers`,
      rapportFiberPvEchec: `Rapport PV Échec: ${transformedData.referenceCount || 0} références`,
      pvEchec: `PV Échec SOR: ${transformedData.fileCount || 0} fichiers`,
      pvHs: `PV Hors Service: ${transformedData.fileCount || 0} fichiers`,
    };

    const message = successMessages[category] || `${category} traité avec succès`;
    this.state.addToasterMessage("success", message);

    // Vérifier si toutes les catégories sont complétées
    this.checkCompletion();
  }

  /**
   * Gérer la suppression des données d'une catégorie
   */
  handleDataRemoved(category) {
    // Mapper vers les sources existantes du state
    const categoryMapping = {
      configTiroirs: "configTiroirs",
      baseROP: "baseROP",
      rapportFiberPvReussite: "rapportFiberPvReussite",
      pvReussite: "pvReussite",
      rapportFiberPvEchec: "rapportFiberPvEchec",
      pvEchec: "pvEchec",
      pvHs: "pvHs",
    };

    const sourceKey = categoryMapping[category];
    if (sourceKey) {
      this.state.setSourceEmpty(sourceKey);
      delete this.savedData.assignedItems[category];
      this.savedData.processingComplete = false;
    }

    this.state.addToasterMessage("info", `${category} data cleared from store`);
  }

  /**
   * Vérifier si toutes les catégories requises sont traitées
   */
  checkCompletion() {
    const requiredCategories = [
      "configTiroirs",
      "baseROP",
      "rapportFiberPvReussite",
      "pvReussite",
      "rapportFiberPvEchec",
      "pvEchec",
      "pvHs",
    ];

    const completedCategories = Object.keys(this.savedData.assignedItems);
    const allCompleted = requiredCategories.every((cat) => completedCategories.includes(cat));

    if (allCompleted && !this.savedData.processingComplete) {
      this.savedData.processingComplete = true;
      this.state.addToasterMessage(
        "success",
        "🎉 Configuration globale terminée ! Toutes les catégories sont assignées."
      );
    }
  }

  /**
   * Obtenir le statut global de completion
   */
  getCompletionStatus() {
    const requiredCategories = [
      "configTiroirs",
      "baseROP",
      "rapportFiberPvReussite",
      "pvReussite",
      "rapportFiberPvEchec",
      "pvEchec",
      "pvHs",
    ];

    const completedCategories = Object.keys(this.savedData.assignedItems);
    const completedCount = completedCategories.length;
    const totalCount = requiredCategories.length;
    const percentage = Math.round((completedCount / totalCount) * 100);

    return {
      completed: completedCount,
      total: totalCount,
      percentage: percentage,
      isComplete: completedCount === totalCount,
      missing: requiredCategories.filter((cat) => !completedCategories.includes(cat)),
    };
  }

  /**
   * Obtenir les données de toutes les catégories
   */
  getAllCategoryData() {
    return this.savedData.assignedItems;
  }

  /**
   * Obtenir les données d'une catégorie spécifique
   */
  getCategoryData(category) {
    return this.savedData.assignedItems[category]?.transformedData || null;
  }

  /**
   * Vérifier si une catégorie est traitée
   */
  isCategoryProcessed(category) {
    return this.savedData.assignedItems.hasOwnProperty(category);
  }

  /**
   * Réinitialiser la configuration globale
   */
  reset() {
    this.components.upload.reset();

    // Réinitialiser aussi les données sauvegardées
    this.savedData = {
      assignedItems: {},
      uploadedFiles: [],
      processingComplete: false,
    };

    // Nettoyer tous les sources du state
    const sources = [
      "configTiroirs",
      "baseROP",
      "rapportFiberPvReussite",
      "pvReussite",
      "rapportFiberPvEchec",
      "pvEchec",
      "pvHs",
    ];

    sources.forEach((sourceKey) => {
      this.state.setSourceEmpty(sourceKey);
    });

    this.state.addToasterMessage("info", "Configuration globale réinitialisée");
  }

  /**
   * Sauvegarder l'état avant navigation
   */
  saveState() {
    if (this.components.upload) {
      const uploadState = {
        assignedItems: this.components.upload.getAssignedItems(),
        uploadedFiles: this.components.upload.uploadedFiles,
      };

      this.savedData = {
        ...this.savedData,
        ...uploadState,
      };
    }
  }

  /**
   * Restaurer l'état après navigation
   */
  restoreState() {
    if (this.savedData.uploadedFiles.length > 0 && this.components.upload) {
      // Restaurer les fichiers uploadés
      this.components.upload.uploadedFiles = [...this.savedData.uploadedFiles];

      // Restaurer les assignations
      this.components.upload.assignedItems = new Map(Object.entries(this.savedData.assignedItems));

      // Re-rendre l'interface
      this.components.upload.renderer.renderFileTree();
      this.components.upload.events.bindTreeEvents();
    }
  }

  updateView() {
    if (this.element) {
      const container = this.element.querySelector(".config-content");
      container.innerHTML = this.renderContent();
      this.mountComponents();
    }
  }

  renderContent() {
    return '<div class="upload-container"></div>';
  }

  mountComponents() {
    // Monter le composant d'upload global
    this.components.upload.mount(".upload-container");

    // Restaurer l'état si des données sont disponibles
    this.restoreState();
  }

  render() {
    return `
      <div class="config-page">
        <h2 class="section-title">Configuration Globale</h2>
        <div class="config-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
  }

  mount(container) {
    this.element = document.createElement("div");
    this.element.innerHTML = this.render();
    container.appendChild(this.element.firstElementChild);
    this.mountComponents();
    return this.element.firstElementChild;
  }

  unmount() {
    // Sauvegarder l'état avant démontage
    this.saveState();

    if (this.components.upload) {
      this.components.upload.unmount();
    }
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }

  /**
   * Nettoyer les ressources lors de la fermeture
   */
  destroy() {
    this.unmount();
    this.savedData = null;
    this.components = {};
  }
}
